/**
 * Rutas canónicas del Admin (Shopify-like).
 * Source of truth para paths usado en App, Sidebar, SettingsSidebar, AdminGlobalSearch, etc.
 */

/** Prefijo tipo Shopify: todas las rutas del panel van bajo /store/:storeSlug */
export const STORE_PREFIX = '/store/:storeSlug';

/** Rutas admin (con y sin prefijo store). */
export const ADMIN_PATH_PREFIXES = [
  '/admin',
  '/home',
  '/dashboard',
  '/store',
  '/orders',
  '/products',
  '/customers',
  '/content',
  '/marketing',
  '/discounts',
  '/online-store',
  '/chats',
  '/inboxes',
  '/apps',
  '/settings',
  '/sales',
  '/analytics',
  '/transport',
  '/ads',
  '/m',
] as const;

export function isAdminPath(pathname: string): boolean {
  const normalized = pathname.replace(/\/$/, '') || '/';
  if (normalized.startsWith('/store/') && normalized.length > 7) return true;
  return ADMIN_PATH_PREFIXES.some(
    (p) => normalized === p || normalized.startsWith(p + '/')
  );
}

/** Construye la ruta del panel con store (ej: /store/mitienda/orders). */
export function storePath(storeSlug: string, path: string): string {
  const segment = path.startsWith('/') ? path.slice(1) : path;
  return segment ? `/store/${encodeURIComponent(storeSlug)}/${segment}` : `/store/${encodeURIComponent(storeSlug)}`;
}

/** Path relativo para rutas anidadas bajo /store/:storeSlug (quita la barra inicial). */
export function storeSegment(absolutePath: string): string {
  return absolutePath.startsWith('/') ? absolutePath.slice(1) : absolutePath;
}

export const ROUTES = {
  // CORE
  HOME: '/home',
  CONTROL_CENTER: '/centro-control',
  CONTROL_CENTER_2: '/centro-control-2',
  ORDERS: '/orders',
  ORDERS_ONLINE: '/orders/online',
  /** Detalle de un pedido online — misma ficha que Operaciones pero bajo la sección Pedidos
   *  (sidebar) y limitada a acciones de pedido online (sin venta/entrega/cobranza). */
  ORDERS_ONLINE_DETAIL: '/orders/online/:id',
  ORDERS_ABANDONED: '/orders/abandoned',
  ORDERS_ABANDONED_DETAIL: '/orders/abandoned/:id',
  PRODUCTS_ROOT: '/products',
  PRODUCTS: '/products/products',
  PRODUCTS_NEW: '/products/products/new',
  PRODUCTS_EDIT: '/products/products/:id/edit',
  PRODUCTS_PHOTO_STUDIO: '/products/photo-studio',
  PRODUCTS_MARKETPLACE_READINESS: '/products/marketplace-readiness',
  PRODUCTS_PHOTO_STUDIO_PROJECT: '/products/photo-studio/:productId',
  PRODUCTS_CATEGORIES: '/products/categories',
  PRODUCTS_CATEGORIES_NEW: '/products/categories/new',
  /** Solo slug: /products/categories/bebidas/edit */
  PRODUCTS_CATEGORIES_EDIT: '/products/categories/:slug/edit',
  PRODUCTS_CATEGORIES_EDIT_LEGACY: '/products/categories/id/:id/edit',
  PRODUCTS_TAGS: '/products/tags',
  PRODUCTS_MODIFIERS: '/products/modifiers',
  PRODUCTS_MODIFIERS_NEW: '/products/modifiers/new',
  /** Solo slug: /products/modifiers/extra-queso/edit (si el segmento es UUID se redirige a slug) */
  PRODUCTS_MODIFIERS_EDIT: '/products/modifiers/:slug/edit',
  PRODUCTS_INVENTORY: '/products/inventory',
  /** Solo slug: /products/inventory/cheesecake-de-fresa (producto se resuelve por slug en DB) */
  PRODUCTS_INVENTORY_PRODUCT: '/products/inventory/:slug',
  /** Legacy: por id (redirige a URL con slug cuando se carga el producto) */
  PRODUCTS_INVENTORY_PRODUCT_LEGACY: '/products/inventory/id/:productId',
  PRODUCTS_VARIANTS: '/products/variants',
  PRODUCTS_OFFERS: '/products/offers',
  CUSTOMERS: '/customers',
  CUSTOMER_DETAIL: '/customers/:id',
  CONTENT: '/content',

  /** Módulo Dropshipping (proveedores tipo Dropi/Aliclik: catálogo + fulfillment). Sección
   *  de primer nivel: un proveedor de dropshipping NO es un courier — es fuente de productos
   *  Y fulfillment. Ver ADR 0006. */
  DROPSHIPPING: '/dropshipping',
  /** Una página por proveedor (integraciones distintas). El catálogo es un componente compartido
   *  con el proveedor fijo por ruta — sin tabs. La conexión vive en Ajustes → Integraciones. */
  DROPSHIPPING_DROPI: '/dropshipping/dropi',
  DROPSHIPPING_ALICLIK: '/dropshipping/aliclik',
  /** @deprecated Se conserva y redirige a Dropi para no romper enlaces guardados. */
  DROPSHIPPING_CATALOG: '/dropshipping/catalogo',

  /** Módulo Transporte (couriers, agencias, motorizados) */
  TRANSPORT_AGENCIES: '/transport/agencias',
  TRANSPORT_MOTORIZADOS: '/transport/motorizados',
  /** Detalle de courier bajo el mismo prefijo que la lista (para que el subnav Agencias/Motorizados siga activo). */
  TRANSPORT_AGENCY_COURIER_DETAIL: '/transport/agencias/:courierSlug',
  TRANSPORT_MOTOR_COURIER_DETAIL: '/transport/motorizados/:courierSlug',
  ANALYTICS: '/analytics',
  MARKETING: '/marketing',
  /** Board donde los clientes votan qué se construye (ADR-0017). */
  PROPUESTAS: '/propuestas',
  DISCOUNTS: '/discounts',

  // Agent Platform (Plan §Fase B) — Studio de agentes IA del tenant.
  // Top-level porque "agente" es la unidad de valor que el tenant paga.
  AGENTS: '/agents',
  AGENTS_STUDIO: '/agents/studio',
  AGENTS_STUDIO_DETAIL: '/agents/studio/:agentId',
  AGENTS_CATALOG: '/agents/catalog',
  AGENTS_GHOSTWRITER: '/agents/ghostwriter',
  AGENTS_OUTCOMES: '/agents/outcomes',
  AGENTS_KNOWLEDGE_BASE: '/agents/knowledge-base',
  AGENTS_CEREBRO: '/agents/cerebro',

  // Smarty Ads — módulo completo (legacy + nuevo chat senior).
  // Plans: docs/plan/PLAN-ADS-META-MODULE.md, PLAN-ADS-AI-STRATEGIST.md,
  //        PLAN-ADS-SALEADS-PARITY.md, PLAN-ADS-CHAT-SENIOR.md
  ADS: '/ads',
  ADS_CHAT: '/ads/chat',
  ADS_CHAT_THREAD: '/ads/chat/:threadId',
  ADS_ONBOARDING: '/ads/onboarding',
  ADS_ONBOARDING_CONVERSATIONAL: '/ads/onboarding/chat',
  ADS_CONNECT: '/ads/connect',
  ADS_CONTENT: '/ads/content',
  ADS_SETTINGS: '/ads/settings',
  ADS_STRATEGIES: '/ads/strategies',
  ADS_STRATEGIES_SAVED: '/ads/strategies/saved',
  ADS_STRATEGY_NEW: '/ads/strategies/new',
  ADS_STRATEGY_DETAIL: '/ads/strategies/:strategyId',
  ADS_TESTIMONIALS: '/ads/testimonials',

  // Sales channels
  ONLINE_STORE: '/online-store',
  ONLINE_STORE_THEMES: '/online-store/themes',
  ONLINE_STORE_THEME_EDITOR: '/online-store/themes/editor',
  ONLINE_STORE_PAGES: '/online-store/pages',
  ONLINE_STORE_PAGE_NEW: '/online-store/pages/nueva',
  ONLINE_STORE_PAGE_EDIT: '/online-store/pages/:pageId',
  ONLINE_STORE_NAVIGATION: '/online-store/navigation',
  ONLINE_STORE_PREFERENCES: '/online-store/preferences',
  ONLINE_STORE_LANDING_PAGES: '/online-store/landing-pages',
  ONLINE_STORE_LANDING_EDITOR: '/online-store/landing-pages/:landingPageId/editor',
  WHATSAPP: '/chats',
  WHATSAPP_INBOX: '/chats/inbox',
  WHATSAPP_CONVERSATION: '/chats/inbox/:conversationId',
  WHATSAPP_MACROS: '/chats/macros',
  WHATSAPP_TEAMS: '/chats/teams',
  WHATSAPP_ASSIGNMENT_RULES: '/chats/assignment-rules',
  WHATSAPP_AUTOMATED_MESSAGES: '/chats/automated-messages',
  WHATSAPP_SUPERVISOR: '/chats/supervisor',
  WHATSAPP_SLA: '/chats/sla',
  WHATSAPP_STATS: '/chats/stats',
  WHATSAPP_KNOWLEDGE_BASE: '/chats/knowledge-base',
  WHATSAPP_WEBHOOKS: '/chats/webhooks',
  WHATSAPP_SETTINGS: '/chats/settings',
  WHATSAPP_AGENTS: '/chats/agents',
  WHATSAPP_INBOXES: '/chats/inboxes',

  /**
   * Wizard multi-canal de creación de entrada (Chatwoot-style, future-proof para Web/API/Email).
   * URL canónica bajo `/inboxes/new/*` — no atada a WhatsApp aunque hoy sea el único canal activo.
   */
  NEW_INBOX_ROOT: '/inboxes/new',
  NEW_INBOX_CHANNEL: '/inboxes/new/:channel',
  NEW_INBOX_CONFIGURE: '/inboxes/new/:channel/configure',
  NEW_INBOX_CONNECT: '/inboxes/new/:inboxId/connect',
  NEW_INBOX_COLLABORATORS: '/inboxes/new/:inboxId/collaborators',
  NEW_INBOX_FINISH: '/inboxes/new/:inboxId/finish',
  /** Detalle de entrada (redirige a /connection). */
  WHATSAPP_INBOX_DETAIL: '/chats/inboxes/:inboxId',
  WHATSAPP_INBOX_CONNECTION: '/chats/inboxes/:inboxId/connection',
  WHATSAPP_INBOX_IDENTITY: '/chats/inboxes/:inboxId/identity',
  WHATSAPP_INBOX_COLLABORATORS: '/chats/inboxes/:inboxId/collaborators',
  WHATSAPP_INBOX_AVAILABILITY: '/chats/inboxes/:inboxId/availability',
  WHATSAPP_INBOX_AI: '/chats/inboxes/:inboxId/ai',
  WHATSAPP_INBOX_CSAT: '/chats/inboxes/:inboxId/csat',
  WHATSAPP_INBOX_ROUTING: '/chats/inboxes/:inboxId/routing',
  WHATSAPP_INBOX_ADVANCED: '/chats/inboxes/:inboxId/advanced',
  WHATSAPP_TAGS: '/chats/tags',
  WHATSAPP_CANNED_RESPONSES: '/chats/canned-responses',
  WHATSAPP_CUSTOM_ATTRIBUTES: '/chats/custom-attributes',
  WHATSAPP_BOTS: '/chats/bots',
  WHATSAPP_AUDIT: '/chats/audit',
  WHATSAPP_CUSTOM_ROLES: '/chats/custom-roles',
  WHATSAPP_CONTACTS: '/chats/contacts',
  WHATSAPP_CONTACT_DETAIL: '/chats/contacts/:contactId',
  WHATSAPP_CAMPAIGNS: '/chats/campaigns',
  WHATSAPP_CSAT: '/chats/csat',
  WHATSAPP_SKILLS: '/chats/skills',

  // Apps (Restaurant Suite)
  APPS_RESTAURANT: '/apps/restaurant',
  APPS_RESTAURANT_POS: '/apps/restaurant/pos',
  APPS_RESTAURANT_KDS: '/apps/restaurant/kds',
  APPS_RESTAURANT_MENU: '/apps/restaurant/menu',
  APPS_RESTAURANT_TABLES: '/apps/restaurant/tables',

  // Apps (Loyalty)
  APPS_LOYALTY: '/apps/loyalty',
  APPS_LOYALTY_DASHBOARD: '/apps/loyalty',
  APPS_LOYALTY_CUSTOMERS: '/apps/loyalty/customers',
  APPS_LOYALTY_REWARDS: '/apps/loyalty/rewards',
  APPS_LOYALTY_HISTORY: '/apps/loyalty/history',
  APPS_LOYALTY_SETTINGS: '/apps/loyalty/settings',
  APPS_LOYALTY_QR_SCANNER: '/apps/loyalty/qr-scanner',

  // Apps (Pedidos COD - Releasit-style)
  APPS_COD: '/apps/cod',
  APPS_COD_DESIGN: '/apps/cod/design',
  APPS_COD_SHIPPING: '/apps/cod/shipping',
  APPS_COD_ANALYTICS: '/apps/cod/analytics',
  APPS_COD_SETTINGS: '/apps/cod/settings',
  APPS_COD_PAYMENTS: '/apps/cod/payments',
  APPS_COD_FRAUD: '/apps/cod/fraud',
  // Operaciones v2 (ADR-001 — nuevo dominio de pedidos; coexiste con COD legacy)
  OPS_PEDIDOS: '/apps/operaciones/pedidos',
  OPS_VENTAS: '/apps/operaciones/ventas',
  OPS_VENTAS_2: '/apps/operaciones/ventas-2',
  OPS_ORDER_NEW: '/apps/operaciones/nuevo-pedido',
  OPS_ORDER_DETAIL: '/apps/operaciones/orden/:id',
  // Páginas vinculadas al pedido (estilo Odoo: smart buttons → registros vinculados con breadcrumbs)
  OPS_ORDER_ENTREGA: '/apps/operaciones/orden/:id/entrega',
  OPS_ORDER_PAGOS: '/apps/operaciones/orden/:id/pagos',
  OPS_ORDER_DOCUMENTOS: '/apps/operaciones/orden/:id/documentos',
  OPS_LOGISTICA: '/apps/operaciones/logistica',
  OPS_COBRANZAS: '/apps/operaciones/cobranzas',
  OPS_CANCELADAS: '/apps/operaciones/canceladas',
  OPS_SETTINGS: '/apps/operaciones/configuracion',
  OPS_DASHBOARD: '/apps/operaciones/resumen',
  OPS_ENTREGAS_RECAUDO: '/apps/operaciones/entregas-recaudo',
  APPS_COD_DELIVERY_SUCCESS: '/apps/cod/delivery-success',
  APPS_COD_SALES_BOOSTER: '/apps/cod/sales-booster',
  APPS_COD_BILLING: '/apps/cod/billing',

  // Apps (CRM y Llamadas)
  APPS_CRM: '/apps/crm',
  APPS_CRM_LEADS: '/apps/crm/leads',
  APPS_CRM_PIPELINE: '/apps/crm/pipeline',
  APPS_CRM_DIALER: '/llamadas/dialer',
  APPS_CRM_CALLS: '/llamadas/calls',
  APPS_CRM_CONVERSION: '/apps/crm/conversion',
  /** Cohorte de recompra: clientes a los que se les acabó un producto y no recompraron. */
  APPS_CRM_RECOMPRA: '/apps/crm/recompra',
  /** Copiloto de CRM: chat operativo IA (solo lectura) — novedades, recompra, pendientes. */
  APPS_CRM_COPILOT: '/apps/crm/copilot',
  APPS_CRM_COPILOT_THREAD: '/apps/crm/copilot/:threadId',
  APPS_CRM_SCHEDULE: '/apps/crm/schedule',
  APPS_CRM_AUTOMATIONS: '/apps/crm/automations',
  APPS_CRM_INBOX: '/apps/crm/inbox',
  APPS_CRM_TASKS: '/apps/crm/tasks',
  APPS_CRM_LEAD_DETAIL: '/apps/crm/leads/:id',
  APPS_CRM_DEAL_DETAIL: '/apps/crm/deals/:id',
  APPS_CRM_EMAIL: '/apps/crm/email',
  APPS_CRM_EMAIL_TEMPLATES: '/apps/crm/email/templates',
  APPS_CRM_SEQUENCES: '/apps/crm/sequences',
  APPS_CRM_SEQUENCE_DETAIL: '/apps/crm/sequences/:id',
  APPS_CRM_DASHBOARD: '/apps/crm/dashboard',
  APPS_CRM_MERGE: '/apps/crm/merge',
  APPS_CALL_CENTER: '/llamadas',
  APPS_CALL_CENTER_SETTINGS: '/llamadas/settings',
  APPS_CALL_CENTER_STATS: '/llamadas/stats',
  APPS_CALL_CENTER_EXTENSIONS: '/llamadas/extensions',
  APPS_CALL_CENTER_CAMPAIGNS: '/llamadas/campaigns',
  APPS_CALL_CENTER_CAMPAIGN_DETAIL: '/llamadas/campaigns/:id',
  APPS_CALL_CENTER_SUPERVISOR: '/llamadas/supervisor',
  APPS_CALL_CENTER_CART_RECOVERY: '/llamadas/cart-recovery',
  APPS_CALL_CENTER_POST_PURCHASE: '/llamadas/post-purchase',
  APPS_CALL_CENTER_COD_CONFIRMATION: '/llamadas/cod-confirmation',
  APPS_CALL_CENTER_COLLECTION: '/llamadas/collection',
  APPS_CALL_CENTER_VOICE_METRICS: '/llamadas/voice-metrics',
  APPS_CALL_CENTER_CALL_ANALYSIS: '/llamadas/call-analysis/:crmCallId',
  APPS_CALL_CENTER_CALL_ANALYSIS_LIST: '/llamadas/test-calls',
  APPS_CALL_CENTER_VOICE_SETTINGS: '/llamadas/voice-settings',

  // Voice AI
  APPS_CRM_VOICE_AI: '/llamadas/voice-ai',
  APPS_CRM_CALL_COACHING: '/llamadas/call-coaching',

  // Report Builder
  APPS_REPORTS: '/apps/reports',
  APPS_REPORTS_DASHBOARDS: '/apps/reports/dashboards',
  APPS_REPORTS_DASHBOARD_DETAIL: '/apps/reports/dashboards/:dashboardId',
  APPS_REPORTS_SAVED: '/apps/reports/saved',

  // Workflow Builder
  APPS_WORKFLOWS: '/apps/workflows',
  APPS_WORKFLOW_DETAIL: '/apps/workflows/:workflowId',

  // Ticketing / Service Desk
  APPS_TICKETS: '/apps/tickets',
  APPS_TICKET_DETAIL: '/apps/tickets/:ticketId',

  // Chatbot Builder
  APPS_CHATBOT: '/apps/chatbot',
  APPS_CHATBOT_EDITOR: '/apps/chatbot/:chatbotId/editor',

  // Apps (Impuestos — "Smarty Impuestos": conexión SUNAT/SIRE, IGV en tiempo real)
  // Plan: docs/plan/PLAN-APP-IMPUESTOS-SUNAT-SIRE.md
  // Contabilidad — módulo unificado: facturación electrónica (CPE) + impuestos (SIRE)
  APPS_CONTABILIDAD: '/apps/contabilidad',                            // resumen fiscal (IGV/SIRE)
  APPS_CONTABILIDAD_COMPROBANTES: '/apps/contabilidad/comprobantes',  // CPE emitidos
  APPS_CONTABILIDAD_COMPROBANTE: '/apps/contabilidad/comprobantes/:id', // detalle de un CPE
  APPS_CONTABILIDAD_CONFIG: '/apps/contabilidad/configuracion',       // conexión SUNAT + emisor + certificado
  // Rutas legadas — redirigen a las nuevas (compatibilidad de bookmarks).
  APPS_IMPUESTOS: '/apps/impuestos',
  APPS_IMPUESTOS_CONEXION: '/apps/impuestos/conexion',
  APPS_CONTABILIDAD_CERT: '/apps/contabilidad/certificado',

  // Mobile CRM + WhatsApp (PWA)
  MOBILE_HOME: '/m/home',
  MOBILE_INBOX: '/m/inbox',
  MOBILE_CHAT: '/m/chat/:id',
  MOBILE_LEADS: '/m/leads',
  MOBILE_DEALS: '/m/deals',
  MOBILE_TASKS: '/m/tasks',
  MOBILE_MORE: '/m/more',
  MOBILE_CONTACT_DETAIL: '/m/contact/:id',
  MOBILE_DEAL_DETAIL: '/m/deal/:id',

  // Table QR (Autopedido)
  TABLE_QR: '/table/:qrToken',
  TABLE_QR_MENU: '/table/:qrToken/menu',
  TABLE_QR_ORDER_CONFIRMATION: '/table/:qrToken/order/:orderId/confirmation',
  TABLE_QR_ORDER_TRACKING: '/table/:qrToken/order/:orderId',

  // Settings
  SETTINGS: '/settings',
  SETTINGS_GENERAL: '/settings/general',
  SETTINGS_BUSINESS: '/settings/business',
  SETTINGS_USERS: '/settings/users',
  SETTINGS_USERS_ROLES: '/settings/users/roles',
  SETTINGS_USERS_SECURITY: '/settings/users/security',
  SETTINGS_PAYMENTS: '/settings/payments',
  SETTINGS_PAYMENT_PROVIDERS: '/settings/payments/providers',
  SETTINGS_CHECKOUT: '/settings/checkout',
  SETTINGS_SHIPPING: '/settings/shipping-and-delivery',
  SETTINGS_DELIVERY_METHODS: '/settings/shipping-and-delivery/delivery-methods',
  SETTINGS_DELIVERY_RULES: '/settings/shipping-and-delivery/delivery-rules',
  SETTINGS_LOGISTICS_COURIER_RULES: '/settings/shipping-and-delivery/courier-rules',
  SETTINGS_LOGISTICS_COURIER_DETAIL: '/settings/shipping-and-delivery/couriers/:courierSlug',
  SETTINGS_SHALOM_AGENCIES: '/settings/shipping-and-delivery/shalom-agencies',
  SETTINGS_SHIPPING_METHODS: '/settings/shipping-and-delivery/shipping-methods',
  SETTINGS_DOMAINS: '/settings/domains',
  SETTINGS_DOMAINS_CONFIGURE: '/settings/domains/configure/:id',
  SETTINGS_BRAND: '/settings/brand',
  SETTINGS_NOTIFICATIONS: '/settings/notifications',
  /** Notificaciones a grupos de WhatsApp por evento (handoff, reclamo, ventas, leads). */
  SETTINGS_NOTIFICATIONS_WHATSAPP: '/settings/whatsapp-notifications',
  /** Reglas de contacto con el CLIENTE (silencio, tope, no-contactar). ADR-0011. */
  SETTINGS_CONTACT_POLICY: '/settings/contacto-clientes',
  SETTINGS_CALL_CENTER: '/settings/call-center',
  SETTINGS_APPS_AND_SALES_CHANNELS: '/settings/apps-and-sales-channels',
  /** Hub de Integraciones (índice de conexión de servicios externos). */
  SETTINGS_INTEGRATIONS: '/settings/integrations',
  /** Detalle/conexión de Shopify (pedidos en tiempo real → Operaciones). */
  SETTINGS_INTEGRATIONS_SHOPIFY: '/settings/integrations/shopify',
  /** Detalle/conexión de Mercado Libre (canal de venta: pedidos + publicar productos). */
  SETTINGS_INTEGRATIONS_MERCADOLIBRE: '/settings/integrations/mercadolibre',
  SETTINGS_INTEGRATIONS_FALABELLA: '/settings/integrations/falabella',
  SETTINGS_INTEGRATIONS_ODOO: '/settings/integrations/odoo',
  /** Preview de homologación de productos de Odoo (vincular/crear/omitir) antes de migrar pedidos. */
  SETTINGS_INTEGRATIONS_ODOO_PRODUCTS: '/settings/integrations/odoo/productos',
  /** Control de la migración masiva de pedidos de Odoo (preview + progreso + pausa). */
  SETTINGS_INTEGRATIONS_ODOO_MIGRATE: '/settings/integrations/odoo/migrar',
  /** Vincular vendedores de Odoo con usuarios de Smarty (atribución de ventas). */
  SETTINGS_INTEGRATIONS_ODOO_SELLERS: '/settings/integrations/odoo/vendedores',
  /** Conexión por proveedor de IA — cada uno su propia página (como Shopify/Odoo/couriers).
   *  Componente compartido con el proveedor fijo por ruta. BYO keys cifradas. Ver ADR 0007. */
  SETTINGS_INTEGRATIONS_OPENAI: '/settings/integrations/openai',
  SETTINGS_INTEGRATIONS_ANTHROPIC: '/settings/integrations/anthropic',
  SETTINGS_INTEGRATIONS_GEMINI: '/settings/integrations/gemini',
  /** @deprecated Página unificada de llaves de IA; ahora cada proveedor tiene la suya. Redirige a OpenAI. */
  SETTINGS_INTEGRATIONS_AI: '/settings/integrations/ia',
  /** Conexión por proveedor de dropshipping — cada uno su propia página (como Shopify/Odoo).
   *  Componente compartido con el proveedor fijo por ruta. Ver ADR 0006/0008. */
  SETTINGS_INTEGRATIONS_DROPI: '/settings/integrations/dropi',
  SETTINGS_INTEGRATIONS_ALICLIK: '/settings/integrations/aliclik',
  /** @deprecated Página unificada de dropshipping; ahora cada proveedor tiene la suya. Redirige a Dropi. */
  SETTINGS_INTEGRATIONS_DROPSHIPPING: '/settings/integrations/dropshipping',
  /** Conexión de couriers — la CONEXIÓN vive en Integraciones (una página por courier, como Shopify/Odoo).
   *  La configuración operativa (agencias, defaults) sigue en Transporte. */
  SETTINGS_INTEGRATIONS_SHALOM: '/settings/integrations/shalom',
  SETTINGS_INTEGRATIONS_EVA: '/settings/integrations/eva',
  SETTINGS_INTEGRATIONS_OLVA: '/settings/integrations/olva',
  /** Hub del canal Marketplaces (sidebar → Canales): estado + configuración general. */
  MARKETPLACES: '/marketplaces',

  /** Rutas de la tienda pública (storefront). Usar estas en el tema para que los enlaces no apunten al admin. */
  STORE_HOME: '/',
  STORE_PRODUCT: '/productos/:id',
  STORE_CART: '/checkout',
  STORE_MENU: '/pedir',
  STORE_PAGE: '/paginas/:slug',
  STORE_ACCOUNT: '/mi-cuenta',
  STORE_PRIVACY: '/paginas/privacidad',
  STORE_TERMS: '/paginas/terminos',
  STORE_SHIPPING: '/paginas/envios',
  STORE_RETURNS: '/paginas/devoluciones',
  STORE_LANDING: '/l/:slug',

  // Legacy / compat (aún usado en algunos lugares)
  SALES_ORDERS: '/sales/orders',
  SALES_HEATMAP: '/sales/heatmap',
  SALES_RANKING: '/sales/ranking',
  SIMULADOR_RENTABILIDAD: '/rentabilidad',
  FINANZAS_PNL: '/finanzas',
  COMISIONES: '/comisiones',
  REPORTES: '/reportes',

  // Settings placeholders (Shopify-like)
  SETTINGS_PLAN: '/settings/plan',
  SETTINGS_BILLING: '/settings/billing',
  SETTINGS_TAXES_AND_DUTIES: '/settings/taxes-and-duties',
  SETTINGS_LOCATIONS: '/settings/locations',
  SETTINGS_CUSTOM_DATA: '/settings/custom-data',
  SETTINGS_LANGUAGES: '/settings/languages',
  SETTINGS_POLICIES: '/settings/policies',
} as const;

type _RoutePath = (typeof ROUTES)[keyof typeof ROUTES];

/**
 * Prefijos de rutas "workspace" que activan el sidebar en modo rail (56px, solo íconos).
 * Son páginas inmersivas (inbox, pipeline, dialer) que necesitan máximo viewport.
 */
export const WORKSPACE_ROUTE_PREFIXES = [
  '/chats/inbox',
  '/apps/crm/inbox',
  '/apps/crm/pipeline',
  '/apps/crm/deals',       // detalle de deal usa panel lateral
] as const;

/** Verifica si un pathname corresponde a una ruta workspace (auto-rail). */
export function isWorkspaceRoute(pathname: string): boolean {
  // Quitar prefijo /store/:slug si existe
  const stripped = pathname.replace(/^\/store\/[^/]+/, '');
  return WORKSPACE_ROUTE_PREFIXES.some(
    (prefix) => stripped === prefix || stripped.startsWith(prefix + '/')
  );
}

/** Convierte nombre de producto a segmento URL (minúsculas, guiones, sin acentos). */
export function slugifyProductName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '') || 'producto';
}

/** Slug para modificadores (misma lógica que slugifyProductName). */
export function slugifyModifierName(name: string): string {
  const s = slugifyProductName(name);
  return s || 'modificador';
}

/** Slug para courier en URL (nombre → minúsculas, guiones). Si la columna slug existe en DB se puede usar esa; si no, se deriva del nombre. */
export function slugifyCourierName(name: string): string {
  const s = slugifyProductName(name);
  return s || 'courier';
}

/**
 * Funciones helper para construir rutas dinámicas con parámetros
 */
export const routeHelpers = {
  /**
   * Construye la ruta de edición de producto con el ID
   */
  productEdit: (id: string | number): string => {
    return ROUTES.PRODUCTS_EDIT.replace(':id', String(id));
  },

  /**
   * Construye la ruta de edición de categoría por slug (URL sin UUID)
   */
  categoryEdit: (slug: string): string => {
    return `/products/categories/${encodeURIComponent(slug)}/edit`;
  },

  /**
   * Detalle de inventario (entradas/salidas) de un producto.
   * Solo slug en la URL: /products/inventory/cheesecake-de-fresa
   */
  productInventoryDetail: (slug: string): string => {
    return `/products/inventory/${encodeURIComponent(slug)}`;
  },

  /**
   * Editar modificador por slug (URL legible)
   */
  modifierEdit: (slug: string): string => {
    return `/products/modifiers/${encodeURIComponent(slug)}/edit`;
  },

  /** Detalle de entrada WhatsApp por id, sección opcional (connection por default). */
  whatsappInboxDetail: (
    inboxId: string,
    section:
      | 'connection'
      | 'identity'
      | 'collaborators'
      | 'availability'
      | 'ai'
      | 'csat'
      | 'routing'
      | 'advanced' = 'connection',
  ): string => `/chats/inboxes/${encodeURIComponent(inboxId)}/${section}`,

  /** Rutas del wizard de creación de entrada multi-canal. */
  newInboxChannel: (channel: 'whatsapp' | 'web' | 'api' | 'email'): string =>
    `/inboxes/new/${channel}`,
  newInboxConfigure: (channel: 'whatsapp' | 'web' | 'api' | 'email', provider?: string): string =>
    `/inboxes/new/${channel}/configure${provider ? `?p=${encodeURIComponent(provider)}` : ''}`,
  newInboxConnect: (inboxId: string): string =>
    `/inboxes/new/${encodeURIComponent(inboxId)}/connect`,
  newInboxCollaborators: (inboxId: string): string =>
    `/inboxes/new/${encodeURIComponent(inboxId)}/collaborators`,
  newInboxFinish: (inboxId: string): string =>
    `/inboxes/new/${encodeURIComponent(inboxId)}/finish`,

  /** URL de producto en la tienda pública */
  storeProduct: (id: string): string => `/productos/${id}`,
  /** URL de página de contenido en la tienda */
  storePage: (slug: string): string => `/paginas/${slug}`,

  /** Detalle de courier por slug (URL legible: /settings/shipping-and-delivery/couriers/shalom) */
  courierDetail: (slug: string): string =>
    `/settings/shipping-and-delivery/couriers/${encodeURIComponent(slug)}`,

  /** Detalle desde Transporte → Agencias (URL bajo /transport/agencias/…). */
  transportAgencyCourierDetail: (slug: string): string =>
    `/transport/agencias/${encodeURIComponent(slug)}`,

  /** Detalle desde Transporte → Motorizados. */
  transportMotorCourierDetail: (slug: string): string =>
    `/transport/motorizados/${encodeURIComponent(slug)}`,

  /** Agent Platform — Detalle del agente IA del tenant. Plan §Fase B.2. */
  agentStudioDetail: (agentId: string): string =>
    `/agents/studio/${encodeURIComponent(agentId)}`,
};

